import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './corporations-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import CorporationsTable, { CORP_COLUMN_DEFS } from './components/CorporationsTable.jsx';
import CorporationDrawer from './components/CorporationDrawer.jsx';
// Shared components from residents (fully generic)
import ActiveFilterBar from '../../community/residents/components/ActiveFilterBar.jsx';
import ColumnVisibilityToggle from '../../community/residents/components/ColumnVisibilityToggle.jsx';
import TableSkeleton from '../../community/residents/components/TableSkeleton.jsx';
// V2 data layer
import {
  getSession, getCampusId, getUserId, getSessionId, getRoleWeight,
} from '../../../services/session.js';
import {
  fetchCorporations, fetchUserRoles, addCorporation,
} from './api/corporationsApi.js';
import { exportCorporationsCsv } from '../../../services/csvExport.js';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
// V1 helpers for impersonation (proven, session-aware)
import {
  iAmHigherAuthority as iAmHigherAuth,
  setRoleInfo,
  reloadSessionCorp,
} from '../../../libs.jsx';

/** All column keys */
const ALL_COLUMN_KEYS = CORP_COLUMN_DEFS.map((c) => c.key);

/** All columns visible by default (only 2 columns) */
const DEFAULT_VISIBLE = [...ALL_COLUMN_KEYS];

/**
 * CorporationsPage — V2 cross-corporation admin overview.
 *
 * Read + Add, with checkboxes for bulk export, and Impersonate
 * in the per-row 3-dot kebab menu (only when the corp has campuses).
 */
const CorporationsPage = () => {
  // ==========================================================================
  // SESSION
  // ==========================================================================
  const campusId = useMemo(() => getCampusId(), []);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const addRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);

  /** Builds the session params object used by API calls */
  const getSessionParams = useCallback(() => ({
    campusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [campusId]);

  // ==========================================================================
  // TOAST
  // ==========================================================================
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // DATA STATE
  // ==========================================================================
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortColumn, setSortColumn] = useState('corporate_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();
  const [visibleColumns, handleColumnToggle] = usePersistedColumns('corporations', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);
  const [selectedIds, setSelectedIds] = useState([]);

  // Drawer + support data
  const [userRoles, setUserRoles] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Impersonate
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [impersonateRoleWeight, setImpersonateRoleWeight] = useState(myRoleWeight);
  const [impersonating, setImpersonating] = useState(false);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  const loadCorporations = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchCorporations(campusId, true);
      setData(result || []);
    } catch (err) {
      console.error('[CorporationsPage] Load corporations error:', err);
      showToast('error', 'Failed to load corporations');
    } finally {
      setLoading(false);
    }
  }, [campusId, showToast]);

  const loadUserRoles = useCallback(async () => {
    try {
      const result = await fetchUserRoles(campusId);
      setUserRoles(result || []);
    } catch (err) {
      console.error('[CorporationsPage] Load user roles error:', err);
    }
  }, [campusId]);

  useEffect(() => {
    loadCorporations();
    loadUserRoles();
  }, [loadCorporations, loadUserRoles]);

  // ==========================================================================
  // FILTER / SORT PIPELINE
  // ==========================================================================
  const filtered = useMemo(() => {
    let result = [...data];

    // 1. Text search
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((r) =>
        r.corporate_name?.toLowerCase().includes(s) ||
        r.campus_name?.toLowerCase().includes(s)
      );
    }

    // 2. Column filters
    Object.entries(filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      result = result.filter((row) => {
        const cellVal = row[columnKey];
        return values.includes(cellVal != null && cellVal !== '' ? String(cellVal) : '(empty)');
      });
    });

    // 3. Sort
    result.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [data, search, filters, sortColumn, sortOrder]);

  // ==========================================================================
  // PAGINATION
  // ==========================================================================
  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, safePage, perPage]);

  const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
  const showingEnd = Math.min(safePage * perPage, totalFiltered);

  useEffect(() => { setPage(1); }, [search, filters, perPage]);

  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [safePage, totalPages]);

  /** Column labels for ActiveFilterBar */
  const columnLabels = useMemo(() => {
    const map = {};
    CORP_COLUMN_DEFS.forEach((c) => { map[c.key] = c.label; });
    return map;
  }, []);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleSort = useCallback((key) => {
    setSortColumn((prev) => {
      if (prev === key) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return key;
    });
  }, []);

  const handleFilterChange = useCallback((columnKey, selectedValues) => {
    setFilters((prev) => ({ ...prev, [columnKey]: selectedValues }));
  }, []);

  const handleRemoveFilter = useCallback((columnKey) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  // Selection
  const handleSelectRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = paginated.map((r) => r.corporate_id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginated, selectedIds]);

  // Export
  const handleExportAll = useCallback(() => {
    if (filtered.length === 0) {
      showToast('error', 'No data to export');
      return;
    }
    exportCorporationsCsv(filtered, 'corporations.csv');
    showToast('success', `Exported ${filtered.length} corporations`);
  }, [filtered, showToast]);

  const handleBulkExport = useCallback(() => {
    const selected = data.filter((d) => selectedIds.includes(d.corporate_id));
    if (selected.length) {
      exportCorporationsCsv(selected, 'corporations-selected.csv');
      showToast('success', `Exported ${selected.length} corporations`);
    }
  }, [selectedIds, data, showToast]);

  // Add Corporation
  const handleAddCorporation = useCallback(async (formData) => {
    const session = getSessionParams();
    const [success, errMsg] = await addCorporation(formData, session);
    if (success) {
      showToast('success', `${formData.corporate_name} has been added`);
      setDrawerOpen(false);
      loadCorporations();
    } else {
      showToast('error', `Failed to add corporation: ${errMsg || 'Unknown error'}`);
    }
  }, [getSessionParams, showToast, loadCorporations]);

  // Impersonate
  const handleImpersonate = useCallback((corporation) => {
    setImpersonateTarget(corporation);
    setImpersonateRoleWeight(myRoleWeight);
    setImpersonating(false);
  }, [myRoleWeight]);

  const handleConfirmImpersonate = useCallback(() => {
    if (!impersonateTarget) return;
    setImpersonating(true);
    const dData = getSession();
    if (!dData) {
      showToast('error', 'Session not found');
      setImpersonating(false);
      return;
    }

    const selectedWeight = parseInt(impersonateRoleWeight, 10);
    const selectedRole = (userRoles || []).find((r) => r.user_role_weight === selectedWeight);
    setRoleInfo(selectedWeight, dData, selectedRole?.user_role_name || '');

    // Corporate-level impersonation — always use -99 redirect path.
    // ProcessSessionReload will set up acntList with all campuses of this corp
    // and auto-select the first campus.
    reloadSessionCorp(
      impersonateTarget.corporate_id,
      -99,
      dData.cognito,
      dData
    );
  }, [impersonateTarget, impersonateRoleWeight, showToast]);

  /** Filtered role options for impersonate dialog */
  const filteredRoles = useMemo(() => {
    return (userRoles || [])
      .filter((r) => iAmHigherAuth(r.user_role_weight, myRoleWeight))
      .sort((a, b) => (a.user_role_weight || 0) - (b.user_role_weight || 0));
  }, [userRoles, myRoleWeight]);

  // Bulk action bar
  const renderBulkBar = () => {
    if (selectedIds.length === 0) return null;
    return (
      <div className="dp-bulk-bar">
        <div className="dp-bulk-bar__left">
          <span className="dp-bulk-bar__count">
            {selectedIds.length} corporation{selectedIds.length !== 1 ? 's' : ''} selected
          </span>
          <button type="button" onClick={() => setSelectedIds([])} className="dp-bulk-bar__deselect">
            Deselect All
          </button>
        </div>
        <div className="dp-bulk-bar__right">
          <button type="button" onClick={handleBulkExport} className="dp-bulk-btn">
            <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export
          </button>
        </div>
      </div>
    );
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="dp-page">
      {/* Page-level toast notification */}
      {toast ? (
        <div
          className={`dp-toast ${toast.type === 'success' ? 'dp-toast--success' : 'dp-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
            className="dp-toast__close"
            aria-label="Dismiss"
          >
            <svg className="dp-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : null}

      {/* ============================================================
          PAGE HEADER
          ============================================================ */}
      <div className="dp-page-header">
        <div className="dp-page-header__inner">
          <div className="dp-breadcrumb-row">
            <div className="dp-breadcrumb">
              <span className="dp-breadcrumb__text">Customers</span>
              <svg className="dp-icon-md dp-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="dp-breadcrumb__current">Corporations</span>
            </div>
          </div>
          <h1 className="dp-page-title">Corporations</h1>
        </div>
      </div>

      {/* ============================================================
          CONTENT
          ============================================================ */}
      <div className="dp-content">
        <div className="dp-controls">
          <div className="dp-controls__left">
            <div className="dp-search">
              <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="search"
                placeholder="Search corporations..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dp-search__input"
                aria-label="Search corporations"
              />
            </div>
          </div>
          <div className="dp-controls__right">
            <ColumnVisibilityToggle columns={CORP_COLUMN_DEFS} visibleColumns={visibleColumns} onToggle={handleColumnToggle} />
            <button type="button" className="dp-btn dp-btn--secondary" onClick={handleExportAll} aria-label="Export corporations">
              <svg className="dp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Export
            </button>
            {addRights ? (
              <button
                type="button"
                className="dp-btn dp-btn--primary"
                onClick={() => setDrawerOpen(true)}
              >
                <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                New Corp
              </button>
            ) : null}
          </div>
        </div>
        <ActiveFilterBar filters={filters} columnLabels={columnLabels} onRemoveFilter={handleRemoveFilter} onClearAll={handleClearAllFilters} />
        {renderBulkBar()}
        {loading ? (
          <TableSkeleton />
        ) : (
          <CorporationsTable
            data={paginated}
            allData={data}
            visibleColumns={visibleColumns}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={handleSort}
            filters={filters}
            onFilterChange={handleFilterChange}
            selectedIds={selectedIds}
            onSelectRow={handleSelectRow}
            onSelectAll={handleSelectAll}
            onImpersonate={handleImpersonate}
          />
        )}
        {/* Pagination */}
        <div className="dp-pagination">
          <p className="dp-pagination__info">
            Showing <span className="dp-pagination__emphasis">{showingStart}</span>&ndash;<span className="dp-pagination__emphasis">{showingEnd}</span> of{' '}
            <span className="dp-pagination__emphasis">{totalFiltered}</span> corporations
          </p>
          <div className="dp-pagination__controls">
            <div className="dp-per-page">
              <label htmlFor="dp-crp-per-page" className="dp-per-page__label">Rows per page:</label>
              <select
                id="dp-crp-per-page"
                value={perPage}
                onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                className="dp-per-page__select"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={300}>300</option>
              </select>
            </div>
            <nav className="dp-page-nav" aria-label="Corporations pagination">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="dp-page-btn"
                aria-label="Previous page"
              >
                <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              {pageNumbers.map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setPage(num)}
                  className={`dp-page-num ${num === safePage ? 'is-active' : ''}`}
                  aria-label={`Page ${num}`}
                  aria-current={num === safePage ? 'page' : undefined}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="dp-page-btn"
                aria-label="Next page"
              >
                <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Corporation Drawer (Add / Migrate) */}
      <CorporationDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleAddCorporation}
      />

      {/* Impersonate Dialog */}
      {impersonateTarget ? (
        <div className="dp-drawer-backdrop" onClick={() => setImpersonateTarget(null)} aria-hidden="true">
          <div className="crp-impersonate-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Impersonate">
            <h3 className="crp-impersonate-dialog__title">Impersonate</h3>
            <p className="crp-impersonate-dialog__corp">
              {impersonateTarget.corporate_name}
              {impersonateTarget.campus_name ? (
                <span className="crp-impersonate-dialog__campuses"> — {impersonateTarget.campus_name}</span>
              ) : null}
            </p>
            <div className="dp-drawer__field">
              <label htmlFor="crp-impersonate-role" className="dp-drawer__label">
                Impersonating User Role
              </label>
              <select
                id="crp-impersonate-role"
                value={impersonateRoleWeight}
                onChange={(e) => setImpersonateRoleWeight(e.target.value)}
                className="dp-drawer__select"
              >
                <option value={myRoleWeight}>My Role</option>
                {filteredRoles.map((r) => (
                  <option key={r.user_role_weight} value={r.user_role_weight}>
                    {r.user_role_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="crp-impersonate-dialog__actions">
              <button
                type="button"
                onClick={() => setImpersonateTarget(null)}
                className="dp-btn dp-btn--secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmImpersonate}
                disabled={impersonating}
                className="dp-btn dp-btn--primary"
              >
                {impersonating ? 'Switching...' : 'Impersonate'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </div>
    </div>
    </>
  );
};

export default CorporationsPage;
